// BBS Correlation & Predictive Analytics Dashboard
// ----------------------------------------------------------------------------
// Single-form (BBS) deep-dive aggregated across ALL companies in the active
// date range. Layout mirrors the reference screenshot:
//
//   Row 0: 6 KPI tiles (Total BBS Forms · Safe / Concern Observations ·
//          Failed Audit Items · Jobs Observed · Overall Risk Score)
//   Row 1: Top Jobs by BBS Submissions | Concern Rate % by Category |
//          Concern Rate % by Work Group
//   Row 2: Concern Rate Trend Over Time | Concern Rate vs Failed Audit Items
//          (correlation) | Predictive Trend (Concern Rate vs Incidents)
//   Row 3: Top 5 Concern Categories | Concern Rate % by Job (Top 10) |
//          Predictive Risk Score by Job
//   Bottom: Focus Areas / Predictive Alert / Recommendation strip
//
// All metrics come from `rows` (already filtered by the parent dashboard).
// BBS Safe / Concern observations live in `row.bbs_meta` keyed by the four
// buckets — PPE, Body Position, Work Environment, Work Procedure — and each
// bucket is an array of `{label, answer:"S"|"C"}` (mirrors the existing
// dashboard's reader, so this stays in sync with the rest of the app).

import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import api from "../../api/api";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  ComposedChart,
  Line,
  Scatter,
  ScatterChart,
  ReferenceLine,
  LineChart,
  LabelList,
} from "recharts";

// ---------------------------------------------------------------------------
// Local UI primitives
// ---------------------------------------------------------------------------
const Card = ({ children, className = "" }) => (
  <div className={`rounded-2xl border border-gray-200 bg-white p-4 shadow-sm ${className}`}>
    {children}
  </div>
);

const SectionTitle = ({ title, subtitle }) => (
  <div className="mb-3">
    <h2 className="text-sm font-bold text-[#0A0A0A] text-left uppercase tracking-wide">
      {title}
    </h2>
    {subtitle && <p className="text-[11px] text-[#717182] text-left">{subtitle}</p>}
  </div>
);

const KpiTile = ({ label, value, sub, accent = "#0A0A0A", trend }) => (
  <div className="flex-1 min-w-[150px] rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 text-left">
      {label}
    </div>
    <div className="mt-1 flex items-baseline gap-2">
      <span className="text-3xl font-bold" style={{ color: accent }}>{value}</span>
      {sub && <span className="text-xs text-gray-500">{sub}</span>}
    </div>
    {trend !== undefined && trend !== null && (
      <div
        className={`text-[11px] mt-1 ${
          trend > 0 ? "text-green-600" : trend < 0 ? "text-red-500" : "text-gray-500"
        }`}
      >
        {trend > 0 ? "▲" : trend < 0 ? "▼" : "—"} {Math.abs(trend).toFixed(1)}% vs prior period
      </div>
    )}
  </div>
);

const EmptyChart = ({ height = 240, message = "No data in range" }) => (
  <div style={{ height }} className="flex items-center justify-center text-xs text-gray-400 text-center px-4">
    {message}
  </div>
);

// Single-line Y-axis tick for category (job) labels — prevents recharts from
// wrapping long names onto multiple squeezed lines. Very long labels are
// truncated with an ellipsis and the full text is shown on hover.
const SingleLineTick = ({ x, y, payload, max = 34 }) => {
  const label = String(payload?.value ?? "");
  const truncated = label.length > max;
  const text = truncated ? `${label.slice(0, max - 1)}…` : label;
  return (
    <text x={x} y={y} dy={3} textAnchor="end" fontSize={10} fill="#4B5563">
      {text}
      {truncated && <title>{label}</title>}
    </text>
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const BBS_BUCKETS = ["PPE", "Body Position", "Work Environment", "Work Procedure"];
const BUCKET_COLORS = {
  "Work Environment": "#DC2626",
  "Work Procedure": "#F97316",
  PPE: "#F59E0B",
  "Body Position": "#16A34A",
};

const getRowDate = (row) => {
  if (row.created_at) return dayjs(row.created_at);
  if (row.filled_date) return dayjs(row.filled_date);
  return null;
};

const normalizeJobName = (s) => String(s || "").replace(/-\(\d+\)\s*$/, "").trim();

// Walks a BBS row and emits per-bucket and per-item tallies of Safe/Concern.
// Falls back to json_data for any rows missing bbs_meta entirely.
const tallyBbs = (row) => {
  let safe = 0;
  let concern = 0;
  const categories = {}; // canonical item label → { S, C }
  const workGroups = {}; // bucket name (PPE/Body Position/...) → { S, C }

  const meta = row?.bbs_meta;
  if (meta && typeof meta === "object") {
    Object.entries(meta).forEach(([bucket, arr]) => {
      if (!Array.isArray(arr)) return;
      const wg = workGroups[bucket] || { S: 0, C: 0 };
      arr.forEach((it) => {
        const ans = it?.answer;
        const lbl = (it?.label || "").trim() || "Other";
        if (ans !== "S" && ans !== "C") return;
        const cat = categories[lbl] || { S: 0, C: 0 };
        if (ans === "S") {
          safe += 1; wg.S += 1; cat.S += 1;
        } else {
          concern += 1; wg.C += 1; cat.C += 1;
        }
        categories[lbl] = cat;
      });
      workGroups[bucket] = wg;
    });
  }

  // Fallback to json_data if bbs_meta produced nothing.
  if (safe + concern === 0) {
    const arr = Array.isArray(row?.json_data) ? row.json_data : [];
    arr.forEach((e) => {
      const a = e?.answer;
      const lbl = (e?.question || "").trim() || "Other";
      if (a === "S" || a === "Safe") {
        safe += 1;
        categories[lbl] = categories[lbl] || { S: 0, C: 0 };
        categories[lbl].S += 1;
      } else if (a === "C" || a === "Concern") {
        concern += 1;
        categories[lbl] = categories[lbl] || { S: 0, C: 0 };
        categories[lbl].C += 1;
      }
    });
  }

  return { safe, concern, categories, workGroups };
};

// Failed audit items for an SHP row. The analytics feed ships parsed
// `shp_meta` (buckets of YES/NO/N/A answers + infractions_count) and does NOT
// include raw json_data, so we count "NO" answers across every section — the
// same definition the Forms → Safety & Health tab uses. Falls back to the
// legacy json_data shape for any row that predates shp_meta.
const countShpFailed = (row) => {
  let failed = 0;

  const buckets = row?.shp_meta?.buckets;
  if (buckets && typeof buckets === "object") {
    Object.values(buckets).forEach((arr) => {
      if (!Array.isArray(arr)) return;
      arr.forEach((it) => {
        if (String(it?.answer ?? "").trim().toUpperCase() === "NO") failed += 1;
      });
    });
    return failed;
  }

  // Legacy fallback (raw json_data) — kept for forms without shp_meta.
  const arr = Array.isArray(row?.json_data) ? row.json_data : [];
  arr.forEach((e) => {
    const a = String(e?.answer ?? "").trim().toLowerCase();
    if (a === "no" || a === "n") failed += 1;
  });
  return failed;
};

const pearson = (pairs) => {
  if (!Array.isArray(pairs) || pairs.length < 2) return null;
  const n = pairs.length;
  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0, sumY2 = 0;
  for (const [x, y] of pairs) {
    sumX += x; sumY += y; sumXY += x * y; sumX2 += x * x; sumY2 += y * y;
  }
  const num = n * sumXY - sumX * sumY;
  const den = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));
  if (!isFinite(den) || den === 0) return null;
  return num / den;
};

const linearFit = (pairs) => {
  if (!Array.isArray(pairs) || pairs.length < 2) return null;
  const n = pairs.length;
  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
  for (const [x, y] of pairs) {
    sumX += x; sumY += y; sumXY += x * y; sumX2 += x * x;
  }
  const denom = n * sumX2 - sumX * sumX;
  if (denom === 0) return null;
  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;
  return { slope, intercept };
};

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

const CorrelationAnalytics = ({ rows = [], singleJob = false }) => {
  // Resolve company id → name so labels read nicely.
  const [companyNames, setCompanyNames] = useState({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/v1/timesheets/company-details");
        const list = res?.data?.data || res?.data || [];
        const map = {};
        (Array.isArray(list) ? list : []).forEach((c) => {
          if (c?.id !== undefined && c?.name) map[String(c.id)] = c.name;
        });
        if (!cancelled) setCompanyNames(map);
      } catch (err) { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // -------------------- Partition --------------------
  const bbsRows = useMemo(() => rows.filter((r) => r.form_type === "BBS"), [rows]);
  const shpRows = useMemo(() => rows.filter((r) => r.form_type === "SHP"), [rows]);
  const aifRows = useMemo(() => rows.filter((r) => r.form_type === "AIF"), [rows]);

  // -------------------- Roll-up across ALL companies --------------------
  const rollup = useMemo(() => {
    let safe = 0, concern = 0;
    const categories = {}; // label → { S, C }
    const workGroups = {}; // bucket → { S, C }
    const perJob = {}; // jobKey → { count, S, C, jobName }
    const perCompany = {}; // companyId → { S, C, forms }

    bbsRows.forEach((r) => {
      const t = tallyBbs(r);
      safe += t.safe;
      concern += t.concern;
      Object.entries(t.categories).forEach(([k, v]) => {
        if (!categories[k]) categories[k] = { S: 0, C: 0 };
        categories[k].S += v.S;
        categories[k].C += v.C;
      });
      Object.entries(t.workGroups).forEach(([k, v]) => {
        if (!workGroups[k]) workGroups[k] = { S: 0, C: 0 };
        workGroups[k].S += v.S;
        workGroups[k].C += v.C;
      });

      // Per-job rollup (key = job_id when present, else normalised name).
      const jobName = normalizeJobName(r.jobDescription);
      const jobId = r.job_id && Number(r.job_id) > 0 ? String(r.job_id) : null;
      const key = jobId
        ? `${jobId} ${jobName || ""}`.trim()
        : jobName || "Unknown";
      if (!perJob[key]) perJob[key] = { jobKey: key, jobId, jobName, count: 0, S: 0, C: 0 };
      perJob[key].count += 1;
      perJob[key].S += t.safe;
      perJob[key].C += t.concern;

      // Per-company rollup so we can split observations by company if needed.
      const cid = String(r.companyid ?? "Unknown");
      if (!perCompany[cid]) perCompany[cid] = { S: 0, C: 0, forms: 0 };
      perCompany[cid].S += t.safe;
      perCompany[cid].C += t.concern;
      perCompany[cid].forms += 1;
    });

    return { safe, concern, categories, workGroups, perJob, perCompany };
  }, [bbsRows]);

  const totalBbs = bbsRows.length;
  const totalObs = rollup.safe + rollup.concern;
  const concernRatePct = totalObs ? (rollup.concern / totalObs) * 100 : 0;
  const safePct = totalObs ? (rollup.safe / totalObs) * 100 : 0;

  // SHP failed audit items across the same range.
  const failedAuditTotal = useMemo(
    () => shpRows.reduce((sum, r) => sum + countShpFailed(r), 0),
    [shpRows],
  );

  // Distinct jobs observed (only count BBS-touched jobs).
  const jobsObserved = useMemo(
    () => Object.keys(rollup.perJob).length,
    [rollup],
  );

  // -------------------- Top Jobs (chart 1) --------------------
  const topJobs = useMemo(() => {
    return Object.values(rollup.perJob)
      // Drop the "Unknown" bucket — rows with no job id and no job name.
      .filter((j) => j.jobId || (j.jobName && j.jobName.trim()))
      .map((j) => {
        const total = j.S + j.C;
        return {
          job: j.jobId
            ? `${j.jobId}${j.jobName ? " - " + j.jobName : ""}`
            : j.jobName || "Unknown",
          count: j.count,
          concernRate: total ? (j.C / total) * 100 : 0,
        };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [rollup]);

  // -------------------- Concern Rate by Category (chart 2) --------------------
  const categoryData = useMemo(() => {
    return Object.entries(rollup.categories)
      .map(([label, v]) => {
        const total = v.S + v.C;
        return {
          category: label,
          safe: v.S,
          concern: v.C,
          concernRate: total ? +((v.C / total) * 100).toFixed(1) : 0,
        };
      })
      .filter((d) => d.safe + d.concern > 0)
      .sort((a, b) => b.concernRate - a.concernRate)
      .slice(0, 10);
  }, [rollup]);

  // -------------------- Concern Rate by Work Group (chart 3) --------------------
  const workGroupData = useMemo(() => {
    return Object.entries(rollup.workGroups)
      .map(([group, v]) => {
        const total = v.S + v.C;
        return {
          group,
          concernRate: total ? +((v.C / total) * 100).toFixed(1) : 0,
        };
      })
      .filter((d) => d.concernRate > 0)
      .sort((a, b) => b.concernRate - a.concernRate);
  }, [rollup]);

  // -------------------- Trend Over Time (chart 4) --------------------
  const trendData = useMemo(() => {
    const byMonth = {};
    bbsRows.forEach((r) => {
      const d = getRowDate(r);
      if (!d) return;
      const key = d.format("YYYY-MM");
      if (!byMonth[key]) byMonth[key] = { key, label: d.format("MMM"), S: 0, C: 0 };
      const t = tallyBbs(r);
      byMonth[key].S += t.safe;
      byMonth[key].C += t.concern;
    });
    return Object.values(byMonth)
      .map((m) => ({
        ...m,
        concernRate: m.S + m.C ? +(((m.C / (m.S + m.C)) * 100).toFixed(1)) : 0,
      }))
      .sort((a, b) => a.key.localeCompare(b.key));
  }, [bbsRows]);

  // Build a robust lookup keyed both by job_id and by normalised job name so
  // BBS↔SHP↔AIF cross-references succeed even when one form has the id and
  // the other only has the description.
  const lookupKeysForJob = (jobId, jobName) => {
    const keys = [];
    const id = jobId && Number(jobId) > 0 ? String(jobId) : null;
    if (id) keys.push(`id:${id}`);
    const nm = normalizeJobName(jobName).toLowerCase();
    if (nm) keys.push(`name:${nm}`);
    return keys;
  };

  // Build the universal job set across BBS + SHP + AIF so the scatter
  // captures every job we have ANY data for — not just BBS-observed jobs.
  // Without this, an AIF row on a job that nobody filed a BBS card for
  // is invisible and the scatter shows incidents=0 even when 28 AIF rows
  // exist in the range.
  const universalJobs = useMemo(() => {
    const byKey = {};
    const visit = (jobId, jobName) => {
      const id = jobId && Number(jobId) > 0 ? String(jobId) : null;
      const nm = normalizeJobName(jobName);
      // Stable key per real-world job: id wins, otherwise normalised name.
      const key = id ? `id:${id}` : (nm ? `name:${nm.toLowerCase()}` : null);
      if (!key) return null;
      if (!byKey[key]) byKey[key] = { key, jobId: id, jobName: nm };
      return byKey[key];
    };
    rows.forEach((r) => visit(r.job_id, r.jobDescription));
    return byKey;
  }, [rows]);

  // -------------------- Scatter: Concern Rate vs Failed Audit Items (chart 5) --------------------
  // One dot per job that appears anywhere in BBS, SHP or AIF rows. X = concern
  // rate (0 if no BBS observations), Y = SHP failed audit items (0 if none).
  const concernVsFailed = useMemo(() => {
    const failedByKey = {};
    shpRows.forEach((r) => {
      const failed = countShpFailed(r);
      if (!failed) return;
      lookupKeysForJob(r.job_id, r.jobDescription).forEach((k) => {
        failedByKey[k] = (failedByKey[k] || 0) + failed;
      });
    });
    const points = [];
    Object.values(universalJobs).forEach((u) => {
      const keys = lookupKeysForJob(u.jobId, u.jobName);
      // Look up BBS rollup if this job has any observations.
      const bbs = keys
        .map((k) => {
          // perJob keys are stored as "<id> <name>" — match by id or name fragment.
          const targetId = k.startsWith("id:") ? k.slice(3) : null;
          const targetName = k.startsWith("name:") ? k.slice(5) : null;
          return Object.values(rollup.perJob).find((j) => {
            if (targetId && String(j.jobId) === targetId) return true;
            if (targetName && (j.jobName || "").toLowerCase() === targetName) return true;
            return false;
          });
        })
        .find(Boolean);
      const S = bbs?.S || 0;
      const C = bbs?.C || 0;
      const total = S + C;
      const failed = keys.reduce(
        (max, k) => Math.max(max, failedByKey[k] || 0),
        0,
      );
      // Skip jobs with zero data on BOTH axes — they add no signal.
      if (total === 0 && failed === 0) return;
      points.push({
        job: u.jobName || u.key,
        concernRate: total ? +((C / total) * 100).toFixed(1) : 0,
        failed,
      });
    });
    return points;
  }, [universalJobs, rollup, shpRows]);

  const concernVsFailedR = useMemo(
    () => pearson(concernVsFailed.map((p) => [p.concernRate, p.failed])),
    [concernVsFailed],
  );
  const concernVsFailedFit = useMemo(
    () => linearFit(concernVsFailed.map((p) => [p.concernRate, p.failed])),
    [concernVsFailed],
  );

  // -------------------- Scatter: Concern Rate vs Incidents (chart 6) --------------------
  // One dot per job that appears anywhere in BBS, SHP or AIF rows. X = concern
  // rate (0 if no BBS observations), Y = AIF incident count (0 if none).
  // Jobs that have AIF incidents but no BBS coverage now appear at X=0,
  // which is exactly the under-observed-job risk signal worth flagging.
  const concernVsIncidents = useMemo(() => {
    const incByKey = {};
    aifRows.forEach((r) => {
      lookupKeysForJob(r.job_id, r.jobDescription).forEach((k) => {
        incByKey[k] = (incByKey[k] || 0) + 1;
      });
    });
    const points = [];
    Object.values(universalJobs).forEach((u) => {
      const keys = lookupKeysForJob(u.jobId, u.jobName);
      const bbs = keys
        .map((k) => {
          const targetId = k.startsWith("id:") ? k.slice(3) : null;
          const targetName = k.startsWith("name:") ? k.slice(5) : null;
          return Object.values(rollup.perJob).find((j) => {
            if (targetId && String(j.jobId) === targetId) return true;
            if (targetName && (j.jobName || "").toLowerCase() === targetName) return true;
            return false;
          });
        })
        .find(Boolean);
      const S = bbs?.S || 0;
      const C = bbs?.C || 0;
      const total = S + C;
      const incidents = keys.reduce(
        (max, k) => Math.max(max, incByKey[k] || 0),
        0,
      );
      if (total === 0 && incidents === 0) return;
      points.push({
        job: u.jobName || u.key,
        concernRate: total ? +((C / total) * 100).toFixed(1) : 0,
        incidents,
      });
    });
    return points;
  }, [universalJobs, rollup, aifRows]);

  const concernVsIncidentsR = useMemo(
    () => pearson(concernVsIncidents.map((p) => [p.concernRate, p.incidents])),
    [concernVsIncidents],
  );
  const concernVsIncidentsFit = useMemo(
    () => linearFit(concernVsIncidents.map((p) => [p.concernRate, p.incidents])),
    [concernVsIncidents],
  );

  // -------------------- Top 5 Concern Categories (chart 7) --------------------
  const topConcernCats = useMemo(
    () =>
      Object.entries(rollup.categories)
        .map(([label, v]) => ({ category: label, concerns: v.C }))
        .filter((c) => c.concerns > 0)
        .sort((a, b) => b.concerns - a.concerns)
        .slice(0, 5),
    [rollup],
  );

  // -------------------- Concern Rate by Job Top 10 (table 8) --------------------
  const concernRateByJob = useMemo(() => {
    return Object.values(rollup.perJob)
      .map((j) => {
        const total = j.S + j.C;
        return {
          job: j.jobName
            ? `${j.jobId ? j.jobId + " - " : ""}${j.jobName}`
            : j.jobKey,
          forms: j.count,
          S: j.S,
          C: j.C,
          concernRate: total ? +((j.C / total) * 100).toFixed(1) : 0,
        };
      })
      .filter((j) => j.S + j.C > 0)
      .sort((a, b) => b.concernRate - a.concernRate)
      .slice(0, 10);
  }, [rollup]);

  // -------------------- Predictive Risk Score by Job (table 9) --------------------
  // Reuse the same id/name lookup used by the scatter charts.
  const incidentsByKey = useMemo(() => {
    const m = {};
    aifRows.forEach((r) => {
      lookupKeysForJob(r.job_id, r.jobDescription).forEach((k) => {
        m[k] = (m[k] || 0) + 1;
      });
    });
    return m;
  }, [aifRows]);

  const predictiveByJob = useMemo(() => {
    return concernRateByJob.map((row) => {
      // Recover id / name from the formatted job string ("123 - Name") so we
      // can rebuild the same keys the lookup is indexed under.
      const [maybeId, ...rest] = row.job.split(" - ");
      const id = /^\d+$/.test((maybeId || "").trim()) ? maybeId.trim() : null;
      const name = id ? rest.join(" - ") : row.job;
      const keys = lookupKeysForJob(id, name);
      const incidents = keys.reduce(
        (max, k) => Math.max(max, incidentsByKey[k] || 0),
        0,
      );
      const score = Math.min(
        100,
        Math.round(row.concernRate * 0.7 + incidents * 10),
      );
      return { ...row, predicted: score, incidents };
    });
  }, [concernRateByJob, incidentsByKey]);

  // -------------------- Overall risk score --------------------
  const overallRiskScore = useMemo(() => {
    const cConcern = Math.min(60, concernRatePct * 2);
    const cFailed = Math.min(25, (failedAuditTotal / Math.max(1, shpRows.length)) * 25);
    const cIncident = Math.min(15, aifRows.length * 3);
    return Math.round(cConcern + cFailed + cIncident);
  }, [concernRatePct, failedAuditTotal, shpRows.length, aifRows.length]);

  const riskBand = overallRiskScore >= 60
    ? { label: "HIGH RISK", color: "#DC2626" }
    : overallRiskScore >= 30
    ? { label: "MEDIUM RISK", color: "#D97706" }
    : { label: "LOW RISK", color: "#16A34A" };

  // Highest-concern bucket (for the side annotation text).
  const topBucket = workGroupData[0];

  // "How is this calculated?" popup for the Predictive Risk Score table.
  const [showRiskInfo, setShowRiskInfo] = useState(false);

  return (
    <div className="space-y-4 bg-gray-100 p-3 rounded-xl">
      {/* Header */}
      <h1 className="text-base font-bold text-left">
        BBS CORRELATION &amp; PREDICTIVE ANALYTICS DASHBOARD
      </h1>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <KpiTile label="Total BBS Forms" value={totalBbs} accent="#0A0A0A" />
        <KpiTile
          label="Safe Observations"
          value={rollup.safe.toLocaleString()}
          sub={`(${safePct.toFixed(1)}%)`}
          accent="#16A34A"
        />
        <KpiTile
          label="Concern Observations"
          value={rollup.concern.toLocaleString()}
          sub={`(${concernRatePct.toFixed(1)}%)`}
          accent="#DC2626"
        />
        <KpiTile label="Failed Audit Items" value={failedAuditTotal} accent="#0A0A0A" />
        {!singleJob && (
          <KpiTile label="Jobs Observed" value={jobsObserved} accent="#0A0A0A" />
        )}
        <div className="flex-1 min-w-[150px] rounded-xl border p-3 shadow-sm" style={{ borderColor: riskBand.color }}>
          <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 text-left">
            Overall Risk Score (All Companies)
          </div>
          <div className="mt-1 flex items-baseline gap-1">
            <span className="text-3xl font-bold" style={{ color: riskBand.color }}>
              {overallRiskScore}
            </span>
            <span className="text-xs text-gray-500">/100</span>
          </div>
          <div className="text-[11px] font-bold mt-1" style={{ color: riskBand.color }}>
            {riskBand.label}
          </div>
        </div>
      </div>

      {/* Row 1: Top Jobs · Concern Rate by Category · Concern Rate by Work Group */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {!singleJob && (
        <Card>
          <SectionTitle title="1. Top Jobs by BBS Submissions" />
          {topJobs.length === 0 ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={topJobs} layout="vertical" margin={{ left: 8, bottom: 24 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 10 }} label={{ value: "# of BBS Forms", position: "insideBottom", offset: -12, fontSize: 10 }} />
                <YAxis type="category" dataKey="job" width={210} tick={<SingleLineTick />} interval={0} />
                <Tooltip />
                <Bar dataKey="count" fill="#155DFC">
                  <LabelList dataKey="count" position="right" style={{ fontSize: 10 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
        )}

        <Card>
          <SectionTitle title={`${singleJob ? "1" : "2"}. Concern Rate % by Category`} subtitle={singleJob ? "This job" : "All Companies"} />
          {categoryData.length === 0 ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={categoryData} margin={{ bottom: 50 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="category" tick={{ fontSize: 9 }} interval={0} angle={-30} textAnchor="end" />
                <YAxis yAxisId="left" tick={{ fontSize: 10 }} label={{ value: "Observations", angle: -90, position: "insideLeft", fontSize: 10 }} />
                <YAxis yAxisId="right" orientation="right" unit="%" tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar yAxisId="left" dataKey="safe" fill="#16A34A" name="Safe" />
                <Bar yAxisId="left" dataKey="concern" fill="#DC2626" name="Concern" />
                <Line yAxisId="right" type="monotone" dataKey="concernRate" stroke="#D97706" strokeWidth={2} name="Concern Rate" dot={{ r: 3 }}>
                  <LabelList dataKey="concernRate" position="top" formatter={(v) => `${v}%`} style={{ fontSize: 9, fill: "#D97706" }} />
                </Line>
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </Card>

      </div>

      {/* Row 2: Work Group · Trend Over Time */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card>
          <SectionTitle title="3. Concern Rate % by Work Group" />
          {workGroupData.length === 0 ? <EmptyChart /> : (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={workGroupData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" unit="%" tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="group" width={120} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="concernRate" radius={[0, 4, 4, 0]}>
                    {workGroupData.map((d, i) => (
                      <LabelList
                        key={`l-${i}`}
                        dataKey="concernRate"
                        position="right"
                        formatter={(v) => `${v}%`}
                        style={{ fontSize: 10 }}
                      />
                    ))}
                    {workGroupData.map((d, i) => (
                      <Bar key={`b-${i}`} fill={BUCKET_COLORS[d.group] || "#0A0A0A"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              {topBucket && (
                <div className="text-[11px] text-gray-600 mt-2 text-left">
                  <b>{topBucket.group}</b> has the highest concern rate
                  ({topBucket.concernRate}%). Focus on housekeeping, fall protection
                  &amp; procedural adherence.
                </div>
              )}
            </>
          )}
        </Card>

        <Card>
          <SectionTitle title="4. Concern Rate % Trend Over Time" subtitle="All Companies" />
          {trendData.length === 0 ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis unit="%" tick={{ fontSize: 10 }} />
                <Tooltip />
                <Line type="monotone" dataKey="concernRate" stroke="#155DFC" strokeWidth={2} dot={{ r: 3 }}>
                  <LabelList dataKey="concernRate" position="top" formatter={(v) => `${v}%`} style={{ fontSize: 9 }} />
                </Line>
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>

      </div>

      {/* Row 3: Scatter correlations 5 + 6 */}
      {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card>
          <SectionTitle
            title="5. Concern Rate vs Failed Audit Items"
            subtitle={
              concernVsFailedR !== null
                ? `R = ${concernVsFailedR.toFixed(2)} ${
                    Math.abs(concernVsFailedR) >= 0.7
                      ? "(Strong Positive Correlation)"
                      : Math.abs(concernVsFailedR) >= 0.4
                      ? "(Moderate)"
                      : "(Weak)"
                  } · As concern rate ↑, failed items ↑.`
                : "Correlation across jobs"
            }
          />
          {concernVsFailed.length === 0 ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={260}>
              <ScatterChart>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" dataKey="concernRate" name="Concern Rate" unit="%" tick={{ fontSize: 10 }} domain={[0, 'dataMax + 5']} />
                <YAxis type="number" dataKey="failed" name="Failed Audit Items" tick={{ fontSize: 10 }} />
                <Tooltip cursor={{ strokeDasharray: "3 3" }} />
                <Scatter data={concernVsFailed} fill="#155DFC" />
                {concernVsFailedFit && (
                  <ReferenceLine
                    stroke="#DC2626"
                    strokeDasharray="4 4"
                    segment={[
                      { x: 0, y: concernVsFailedFit.intercept },
                      { x: 100, y: concernVsFailedFit.intercept + concernVsFailedFit.slope * 100 },
                    ]}
                    ifOverflow="extendDomain"
                  />
                )}
              </ScatterChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card>
          <SectionTitle
            title="6. Predictive Trend: Concern Rate vs Incidents"
            subtitle={
              concernVsIncidentsR !== null
                ? `R = ${concernVsIncidentsR.toFixed(2)}${
                    concernVsIncidentsFit
                      ? ` · slope ${concernVsIncidentsFit.slope.toFixed(2)} · higher concern rate predicts more incidents`
                      : ""
                  }`
                : "Predictive correlation across jobs"
            }
          />
          {concernVsIncidents.length === 0 ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={260}>
              <ScatterChart>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" dataKey="concernRate" name="Concern Rate" unit="%" tick={{ fontSize: 10 }} domain={[0, 'dataMax + 5']} />
                <YAxis type="number" dataKey="incidents" name="Incidents" tick={{ fontSize: 10 }} />
                <Tooltip cursor={{ strokeDasharray: "3 3" }} />
                <Scatter data={concernVsIncidents} fill="#E7000B" />
                {concernVsIncidentsFit && (
                  <ReferenceLine
                    stroke="#0A0A0A"
                    strokeDasharray="4 4"
                    segment={[
                      { x: 0, y: concernVsIncidentsFit.intercept },
                      { x: 100, y: concernVsIncidentsFit.intercept + concernVsIncidentsFit.slope * 100 },
                    ]}
                    ifOverflow="extendDomain"
                  />
                )}
              </ScatterChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div> */}

      {/* Row 4: Top 5 Concern Categories · Concern Rate by Job */}
      {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card>
          <SectionTitle title="7. Top 5 Concern Categories Driving Incidents" subtitle="From BBS observations" />
          {topConcernCats.length === 0 ? <EmptyChart /> : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={topConcernCats} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={{ fontSize: 10 }} label={{ value: "# of Related Incidents", position: "insideBottom", offset: -4, fontSize: 10 }} />
                <YAxis type="category" dataKey="category" width={140} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="concerns" fill="#8B5CF6">
                  <LabelList dataKey="concerns" position="right" style={{ fontSize: 10 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card>
          <SectionTitle title="8. Concern Rate % by Job" subtitle="Top 10 Jobs" />
          {concernRateByJob.length === 0 ? <EmptyChart /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="px-2 py-1">Job</th>
                    <th className="px-2 py-1 text-right">BBS</th>
                    <th className="px-2 py-1 text-right">Concern</th>
                    <th className="px-2 py-1 text-right">Safe</th>
                    <th className="px-2 py-1 text-right">Rate</th>
                    <th className="px-2 py-1 text-right">Risk</th>
                  </tr>
                </thead>
                <tbody>
                  {concernRateByJob.map((j) => {
                    const risk = j.concernRate >= 25 ? "HIGH" : j.concernRate >= 15 ? "MEDIUM" : "LOW";
                    const color = risk === "HIGH" ? "#DC2626" : risk === "MEDIUM" ? "#D97706" : "#16A34A";
                    return (
                      <tr key={j.job} className="border-t">
                        <td className="px-2 py-1 truncate max-w-[140px]" title={j.job}>{j.job}</td>
                        <td className="px-2 py-1 text-right">{j.forms}</td>
                        <td className="px-2 py-1 text-right">{j.C}</td>
                        <td className="px-2 py-1 text-right">{j.S}</td>
                        <td className="px-2 py-1 text-right font-medium">{j.concernRate}%</td>
                        <td className="px-2 py-1 text-right font-bold" style={{ color }}>{risk}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

      </div> */}

      {/* Row 5: Predictive risk by Job (full width) — hidden for a single job */}
      {!singleJob && (
      <div className="grid grid-cols-1 gap-3">
        <Card>
          <div className="flex items-start justify-between">
            <SectionTitle title="5. Predictive Risk Score by Job" subtitle="Next 30 Days" />
            <button
              type="button"
              onClick={() => setShowRiskInfo(true)}
              title="How is this calculated?"
              aria-label="How is this calculated?"
              className="shrink-0 p-1.5 rounded-lg text-gray-500 hover:text-[#155DFC] hover:bg-blue-50 transition"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </button>
          </div>
          {predictiveByJob.length === 0 ? <EmptyChart /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="px-2 py-1">Job</th>
                    <th className="px-2 py-1 text-right">Current Rate</th>
                    <th className="px-2 py-1 text-right">Incidents</th>
                    <th className="px-2 py-1 text-right">Predicted</th>
                    <th className="px-2 py-1 text-right">Risk</th>
                  </tr>
                </thead>
                <tbody>
                  {predictiveByJob.map((p) => {
                    const band = p.predicted >= 60 ? "HIGH" : p.predicted >= 30 ? "MEDIUM" : "LOW";
                    const color = band === "HIGH" ? "#DC2626" : band === "MEDIUM" ? "#D97706" : "#16A34A";
                    return (
                      <tr key={p.job} className="border-t">
                        <td className="px-2 py-1 truncate max-w-[140px]" title={p.job}>{p.job}</td>
                        <td className="px-2 py-1 text-right">{p.concernRate}%</td>
                        <td className="px-2 py-1 text-right">{p.incidents}</td>
                        <td className="px-2 py-1 text-right font-medium">{p.predicted}</td>
                        <td className="px-2 py-1 text-right font-bold" style={{ color }}>{band}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
      )}

      {/* "How is this calculated?" popup for the Predictive Risk Score table */}
      {showRiskInfo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setShowRiskInfo(false)}
        >
          <div
            className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
              <h3 className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wide">
                How the Predictive Risk Score works
              </h3>
              <button
                type="button"
                onClick={() => setShowRiskInfo(false)}
                aria-label="Close"
                className="p-1 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="px-5 py-4 space-y-4 text-left text-[13px] text-gray-700">
              <p className="text-[12px] text-gray-500">
                This is a rule-based score computed from the current date range —
                not a machine-learning forecast. Each column comes straight from
                your BBS and Incident data.
              </p>

              {/* Current Rate */}
              <div>
                <div className="font-bold text-[#0A0A0A]">Current Rate</div>
                <p className="mt-0.5">
                  The job's <b>BBS concern rate</b> — how often observers marked a
                  behaviour as a <i>Concern</i> instead of <i>Safe</i>:
                </p>
                <div className="mt-1 rounded-lg bg-gray-50 px-3 py-2 font-mono text-[12px]">
                  Concern ÷ (Safe + Concern) × 100
                </div>
              </div>

              {/* Predicted */}
              <div>
                <div className="font-bold text-[#0A0A0A]">Predicted</div>
                <p className="mt-0.5">
                  A 0–100 risk score blending the current concern rate with the
                  number of <b>incidents</b> (Accident/Incident forms) filed for
                  that job:
                </p>
                <div className="mt-1 rounded-lg bg-gray-50 px-3 py-2 font-mono text-[12px]">
                  min(100, round(Current&nbsp;Rate × 0.7 + Incidents × 10))
                </div>
                <p className="mt-1 text-[12px] text-gray-500">
                  Each concern-rate point adds 0.7; each past incident adds 10.
                  Example: 30% rate + 3 incidents → 30×0.7 + 3×10 = <b>51</b>.
                </p>
              </div>

              {/* Risk */}
              <div>
                <div className="font-bold text-[#0A0A0A]">Risk</div>
                <p className="mt-0.5">The Predicted score mapped to a band:</p>
                <div className="mt-1.5 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-16 text-center text-[11px] font-bold rounded px-2 py-0.5 text-white" style={{ background: "#DC2626" }}>HIGH</span>
                    <span className="text-[12px]">score ≥ 60</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-16 text-center text-[11px] font-bold rounded px-2 py-0.5 text-white" style={{ background: "#D97706" }}>MEDIUM</span>
                    <span className="text-[12px]">score 30 – 59</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-16 text-center text-[11px] font-bold rounded px-2 py-0.5 text-white" style={{ background: "#16A34A" }}>LOW</span>
                    <span className="text-[12px]">score &lt; 30</span>
                  </div>
                </div>
              </div>

              <p className="text-[11px] text-gray-400 border-t border-gray-100 pt-3">
                The weights (0.7 and 10) and thresholds (30 / 60) are fixed
                constants. "Next 30 Days" flags the job as one to watch based on
                today's data — it is not a time-series projection.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Bottom strip — Focus Areas / Predictive Alert / Recommendation */}
      {/* <div className="rounded-xl bg-[#1E3A8A] text-white text-xs p-4 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="md:col-span-1 flex items-start gap-2">
          <span className="text-yellow-300 text-lg">💡</span>
          <div className="text-left">
            <div className="font-bold mb-0.5">INSIGHT</div>
            Concern rate is a leading indicator. Projects with concern rate &gt; 20% historically show meaningfully higher incident rates — focus mitigations on the top jobs &amp; categories above to reduce risk.
          </div>
        </div>
        <div className="text-left">
          <div className="font-bold mb-1">🎯 FOCUS AREAS</div>
          <ol className="list-decimal ml-4 space-y-0.5">
            {topConcernCats.slice(0, 3).map((c) => (
              <li key={c.category}>{c.category}</li>
            ))}
            {topConcernCats.length === 0 && <li>—</li>}
          </ol>
        </div>
        <div className="text-left">
          <div className="font-bold mb-1">📈 PREDICTIVE ALERT</div>
          {(() => {
            const high = predictiveByJob.filter((p) => p.predicted >= 60).length;
            return high > 0 ? (
              <p>{high} job{high === 1 ? "" : "s"} trending high risk. Early intervention can prevent incidents.</p>
            ) : (
              <p>No jobs currently in the high-risk band.</p>
            );
          })()}
        </div>
        <div className="text-left">
          <div className="font-bold mb-1">🛡 RECOMMENDATION</div>
          <p>
            Increase observations, coaching &amp; corrective actions on the
            highest-concern jobs and on the {topBucket?.group || "top"} work
            group.
          </p>
        </div>
      </div> */}
    </div>
  );
};

export default CorrelationAnalytics;
